import React from 'react';

import {
  ArrowUpward,
  ArrowDownward,
  FilterList,
  WorkOff
} from '@mui/icons-material';
import {
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  styled,
  IconButton,
  TableBody,
  Typography
} from '@mui/material';
import update from 'immutability-helper';
import _ from 'lodash';
import { PopupMenu } from 'modules';

import {
  GooglePaginationControl,
  IPaginationParams,
  StandardPaginationControl
} from '../layer1';



interface IWithId {
  id: string;
}

export interface ISortParams {
  index: number;
  order: 'asc' | 'desc' | 'none';
}

export interface IRowAction<TRow> {
  elIcon?: React.ReactNode;
  variant?: 'default' | 'danger';

  label: string;
  fnShouldHide?: (row: TRow) => boolean;
  fnAction: (id: string) => void;
}

export interface IBatchAction {
  label: string;
  fnAction: (ids: string[]) => void;
}

export interface IColumnDef<T> {
  label: string;
  wordWrap?: boolean;
  fillWidth?: boolean;
  width?: string;
  fnSortValue?: (value: T) => number | string;
  fnRenderCell: (value: T, index: number) => React.ReactElement | React.ReactText;
}



const NoDataCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  borderBottom: 'none',
  height: 250
}));

const TableHeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end'
}));


const TableFooterBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2)
}));



export interface IProps<TRowData> {
  hideTopControl?: boolean;
  data: TRowData[];
  columnDefs: IColumnDef<TRowData>[];
  sort?: ISortParams;
  rowActions?: IRowAction<TRowData>[];
  batchActions?: IBatchAction[];
  canSelectRows?: boolean;
  paginationParams?: IPaginationParams;

  onClickSort?: (params: ISortParams) => void;
  onSelectRow?: (id: string, isSelected: boolean) => void;
}

export const FC = <T extends IWithId>(
  props: IProps<T> & { children?: React.ReactNode; }
) => {
  const [page, setPage] = React.useState(0);
  const [id2IsSelected, setId2IsSelected] = React.useState<_.Dictionary<boolean>>({});
  const selectedIds = _.keys(_.pickBy(id2IsSelected, selected => selected));

  const elPaginationControl = React.useMemo(() => {
    if (_.isNil(props.paginationParams)) {
      return null;
    }

    const { paginationParams } = props;
    if (paginationParams.totalRows < paginationParams.pageSize) {
      return null;
    }

    if (paginationParams.type === 'standard') {
      const currentPageIndex = _.indexOf(
        paginationParams.pageCursors,
        paginationParams.currentPageCursor
      );

      return (
        <StandardPaginationControl
          page={currentPageIndex < 0 ? page : currentPageIndex}
          pageCount={paginationParams.pageCursors.length}
          rowCount={paginationParams.totalRows}
          pageSize={paginationParams.pageSize}
          onPageChanged={pageNumber => {
            paginationParams.onPageChanged(paginationParams.pageCursors[pageNumber]);
            setPage(pageNumber);
          }}
        />
      );
    }

    if (paginationParams.type === 'cursorBased') {
      return (
        <GooglePaginationControl
          rowCount={paginationParams.totalRows}
          pageSize={paginationParams.pageSize}
          onClickPrevious={paginationParams.onPreviousPage}
          onClickNext={paginationParams.onNextPage}
        />
      );
    }
  }, [props]);

  React.useEffect(() => {
    setId2IsSelected(
      _.mapValues(
        _.keyBy(props.data, row => row.id),
        () => false
      )
    );
  }, [props.data]);

  const totalColumns =
    props.columnDefs.length +
    (props.canSelectRows ? 1 : 0) +
    (props.rowActions && props.rowActions.length ? 1 : 0);

  function iconForColumn(index: number) {
    if (index === props.sort?.index) {
      return props.sort.order === 'asc' ? (
        <ArrowUpward />
      ) : props.sort.order === 'desc' ? (
        <ArrowDownward />
      ) : (
        <FilterList />
      );
    }

    return <FilterList />;
  }

  function handleClickSort(index: number) {
    const def = props.columnDefs[index];
    if (props.onClickSort && props.sort && def.fnSortValue) {
      const order =
        index === props.sort.index
          ? props.sort.order === 'asc'
            ? 'desc'
            : props.sort.order === 'desc'
              ? 'none'
              : 'asc'
          : 'asc';

      props.onClickSort({
        index,
        order
      });
    }
  }

  const rowData = React.useMemo(() => {
    if (props.sort) {
      const fnSort = props.columnDefs[props.sort?.index ?? 0]?.fnSortValue;

      if (fnSort && props.sort.order !== 'none') {
        const sortedRows = _.sortBy(props.data, row => fnSort(row));
        return props.sort.order === 'desc' ? _.reverse(sortedRows) : sortedRows;
      }

      return props.data;
    }

    return props.data;
  }, [props.columnDefs, props.data, props.sort]);

  const hasRowActions = React.useMemo(() => !_.isEmpty(props.rowActions ?? []), [props.rowActions]);

  return (
    <TableContainer>
      <TableHeaderBox>
        {!props.hideTopControl && <Box>{elPaginationControl}</Box>}
      </TableHeaderBox>

      <Table size="small">
        <TableHead>
          <TableRow>
            {props.canSelectRows && (
              <TableCell>
                <Checkbox
                  color="secondary"
                  checked={!_.isEmpty(id2IsSelected) && _.every(id2IsSelected)}
                  onChange={() => {
                    const isAllSelected = _.every(id2IsSelected);
                    setId2IsSelected(_.mapValues(id2IsSelected, () => !isAllSelected));
                  }}
                />
              </TableCell>
            )}

            {_.map(props.columnDefs, (def, index) => (
              <TableCell
                key={index}
                style={{
                  whiteSpace: !_.isNil(def.wordWrap) && !def.wordWrap ? 'normal' : 'nowrap'
                }}>
                <b>{def.label}</b>

                {props.sort && props.onClickSort && !!def.fnSortValue && (
                  <IconButton onClick={() => handleClickSort(index)}>
                    {iconForColumn(index)}
                  </IconButton>
                )}
              </TableCell>
            ))}

            {hasRowActions && <TableCell />}
          </TableRow>
        </TableHead>

        <TableBody>
          {_.map(rowData, (rowData, rowIndex) => {
            const rowActions = _.filter(props.rowActions ?? [], act => _.isNil(act.fnShouldHide) || !act.fnShouldHide(rowData));

            return (
              <TableRow key={rowData.id}>
                {props.canSelectRows && (
                  <TableCell>
                    <Checkbox
                      color="secondary"
                      checked={id2IsSelected[rowData.id] ?? false}
                      onChange={ev => {
                        if (props.onSelectRow) {
                          props.onSelectRow(rowData.id, ev.currentTarget.checked);
                        }
                      }}
                      onClick={() => {
                        setId2IsSelected(
                          update(id2IsSelected, {
                            [rowData.id]: { $set: !(id2IsSelected[rowData.id] ?? false) }
                          })
                        );
                      }}
                    />
                  </TableCell>
                )}

                {_.map(props.columnDefs, (def, index) => (
                  <TableCell
                    key={index}
                    style={{
                      width: def.fillWidth ? '100%' : def.width || undefined,
                      whiteSpace: !_.isNil(def.wordWrap) && !def.wordWrap ? 'nowrap' : 'normal'
                    }}>
                    {def.fnRenderCell(rowData, rowIndex)}
                  </TableCell>
                ))}

                {hasRowActions && (
                  <TableCell>
                    <PopupMenu.FC
                      triggerVariant="horiz"
                      options={_.map(rowActions, action => ({
                        type: 'action',
                        label: action.label,
                        fnAction: () => action.fnAction(rowData.id)
                      }))}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}

          {_.isEmpty(props.data) && (
            <TableRow>
              <NoDataCell colSpan={totalColumns}>
                <WorkOff fontSize="large" />
                <Typography variant="body1">
                  There is nothing to show here
                </Typography>
              </NoDataCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TableFooterBox>
        <Box>
          {props.canSelectRows && !_.isEmpty(selectedIds) && (
            <PopupMenu.FC
              triggerVariant="horiz"
              options={_.map(
                props.batchActions,
                (action): PopupMenu.IMenuOption => ({
                  type: 'action',
                  label: action.label,
                  fnAction: () => action.fnAction(selectedIds)
                })
              )}
            />
          )}

          {props.canSelectRows && selectedIds.length > 0 && (
            <Typography variant="caption">{selectedIds.length} items selected</Typography>
          )}
        </Box>

        <Box>{elPaginationControl}</Box>
      </TableFooterBox>
    </TableContainer>
  );
};
